import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useState } from "react";
import { useLoginStatus } from "../../hooks/useLoginStatus";
import useTranslation from "../../hooks/useTranslation";

type VoiceSessionRow = {
  id: number;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number;
  user_name: string;
  username: string;
  situation_label_de: string;
  language: string;
  turn_count: number;
  is_active: boolean;
  active_branch: string;
  turns: Array<{
    role: string;
    text: string;
    timestamp: string;
  }>;
};

type VoiceFailurePatternRow = {
  category: string;
  explanation: string;
  relatedConversations: number;
  languages: string;
  priority: string;
};

type VoiceSuggestedChangeRow = {
  id: string;
  type: string;
  currentPromptVersion: string;
  proposedPromptVersion: string;
  diff: string;
  reason: string;
  evaluationStatus: string;
};

const patterns: VoiceFailurePatternRow[] = [
  {
    category: "language_switching",
    explanation:
      "The agent recognizes the switch but keeps the previous response language for one more turn.",
    relatedConversations: 6,
    languages: "Italian, French, English",
    priority: "high",
  },
  {
    category: "transcription_gap",
    explanation:
      "Short corrections are sometimes missed after an interruption or overlap.",
    relatedConversations: 4,
    languages: "German, Italian",
    priority: "medium",
  },
];

const changes: VoiceSuggestedChangeRow[] = [
  {
    id: "SC-88",
    type: "Prompt edit",
    currentPromptVersion: "voice-prompt-v12",
    proposedPromptVersion: "voice-prompt-v13",
    diff:
      "Tighten the instruction to switch response language immediately after an explicit user request.",
    reason: "Recurring delayed language-switch failures in mixed-language turns.",
    evaluationStatus: "Evaluation ready",
  },
  {
    id: "SC-89",
    type: "New evaluation case",
    currentPromptVersion: "voice-prompt-v12",
    proposedPromptVersion: "voice-prompt-v12",
    diff:
      "Add a sanitized case for short interrupted corrections after a language switch.",
    reason: "Short corrections are being lost in a repeatable way.",
    evaluationStatus: "Proposed",
  },
];

export const Route = createFileRoute("/app/staff_voice_review")({
  component: StaffVoiceReviewPage,
});

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-slate-200 p-4">
      <p className="text-xs font-medium normal-case tracking-[0.08em] text-slate-600">
        {label}
      </p>
      <p className="mt-2 text-2xl font-light tabular-nums text-slate-950">
        {value}
      </p>
    </div>
  );
}

function Badge({
  children,
  tone,
}: {
  children: string;
  tone: "good" | "warn" | "neutral";
}) {
  const className =
    tone === "good"
      ? "bg-emerald-50 text-emerald-800"
      : tone === "warn"
        ? "bg-amber-50 text-amber-800"
        : "bg-slate-50 text-slate-700";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${className}`}
    >
      {children}
    </span>
  );
}

function MetricRow({ label, value }: { label: string; value: string }) {
  return (
    <tr className="border-b border-slate-200 last:border-b-0">
      <th className="px-0 py-2 text-left text-xs font-medium normal-case tracking-[0.08em] text-slate-600">
        {label}
      </th>
      <td className="px-0 py-2 text-right text-sm font-light tabular-nums text-slate-950">
        {value}
      </td>
    </tr>
  );
}

function formatDateTime(iso: string) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(iso));
}

function formatNumber(value: number) {
  return new Intl.NumberFormat(undefined).format(value);
}

function formatConversationTurnLabel(role: string) {
  if (role === "assistant") return "Assistant";
  if (role === "user") return "User";
  return role || "Turn";
}

function StaffVoiceReviewPage() {
  const loginStatus = useLoginStatus();
  const navigate = useNavigate();
  const { translate } = useTranslation();
  const [voiceSessions, setVoiceSessions] = useState<VoiceSessionRow[]>([]);
  const [voiceSessionsState, setVoiceSessionsState] = useState<
    "loading" | "ready" | "error"
  >("loading");
  const [selectedConversationId, setSelectedConversationId] = useState<string>("");
  const [selectedChangeId, setSelectedChangeId] = useState(changes[0]?.id ?? "");
  const isStaff =
    loginStatus.data?.state === "logged in" && loginStatus.data.is_staff;

  useEffect(() => {
    if (loginStatus.isSuccess && !isStaff) {
      navigate({ to: "/app/product_logbook", replace: true });
    }
  }, [isStaff, loginStatus.isSuccess, navigate]);

  useEffect(() => {
    if (!loginStatus.isSuccess || !isStaff) return;
    const controller = new AbortController();
    setVoiceSessionsState("loading");
    fetch("/api/reeprise/staff/voice-sessions/", {
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`voice sessions request failed: ${response.status}`);
        }
        return response.json() as Promise<{ results: VoiceSessionRow[] }>;
      })
      .then((data) => {
        setVoiceSessions(data.results ?? []);
        setSelectedConversationId((current) => current || String(data.results?.[0]?.id ?? ""));
        setVoiceSessionsState("ready");
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.error("Failed to load voice sessions", error);
        setVoiceSessions([]);
        setVoiceSessionsState("error");
      });
    return () => controller.abort();
  }, [isStaff, loginStatus.isSuccess]);

  const selectedConversation = useMemo(
    () =>
      voiceSessions.find((row) => String(row.id) === selectedConversationId) ??
      voiceSessions[0],
    [selectedConversationId, voiceSessions],
  );
  const selectedChange = useMemo(
    () => changes.find((row) => row.id === selectedChangeId) ?? changes[0],
    [selectedChangeId],
  );
  const selectedPattern = patterns[0];
  const reviewedCount = voiceSessions.length;
  const activeCount = voiceSessions.filter((session) => session.is_active).length;
  const totalDuration = voiceSessions.reduce(
    (sum, session) => sum + session.duration_seconds,
    0,
  );
  const uniqueUsers = new Set(voiceSessions.map((session) => session.username)).size;

  if (!loginStatus.isSuccess || !isStaff) {
    return <div className="w-full px-4 py-6 sm:px-8 lg:px-12" />;
  }

  return (
    <div className="w-full px-4 py-6 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-7xl space-y-8">
        <Link
          to="/app/staff_console"
          className="inline-flex items-center gap-2 text-sm font-medium text-slate-700"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          {translate("staff_voice_review_back")}
        </Link>
        <header className="space-y-2">
          <p className="text-xs font-medium normal-case tracking-[0.08em] text-slate-600">
            {translate("staff_voice_review_eyebrow")}
          </p>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-950">
            {translate("staff_voice_review_title")}
          </h1>
          <p className="max-w-3xl text-sm leading-relaxed text-slate-700">
            {translate("staff_voice_review_description")}
          </p>
        </header>
        <section className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <StatTile
              label={translate("staff_voice_review_metric_reviewed")}
              value={formatNumber(reviewedCount)}
            />
            <StatTile
              label={translate("staff_voice_review_metric_corrections")}
              value={formatNumber(activeCount)}
            />
            <StatTile
              label={translate("staff_voice_review_metric_patterns")}
              value={formatNumber(uniqueUsers)}
            />
            <StatTile
              label={translate("staff_voice_review_metric_pending")}
              value={formatNumber(totalDuration)}
            />
          </div>

          <div className="overflow-x-auto">
            {voiceSessionsState === "loading" ? (
              <p className="px-3 py-4 text-sm text-slate-700">
                {translate("staff_voice_review_loading")}
              </p>
            ) : voiceSessionsState === "error" ? (
              <p className="px-3 py-4 text-sm text-slate-700">
                {translate("staff_voice_review_error")}
              </p>
            ) : (
              <table className="min-w-full text-left text-sm">
                <thead className="text-xs normal-case tracking-[0.08em] text-slate-600">
                  <tr className="border-b border-slate-200">
                    <th className="px-3 py-3 font-medium">
                      {translate("staff_voice_review_col_time")}
                    </th>
                    <th className="px-3 py-3 font-medium">
                      {translate("staff_voice_review_col_user")}
                    </th>
                    <th className="px-3 py-3 font-medium">
                      {translate("staff_voice_review_col_language")}
                    </th>
                    <th className="px-3 py-3 font-medium">
                      {translate("staff_voice_review_col_duration")}
                    </th>
                    <th className="px-3 py-3 font-medium">
                      {translate("staff_voice_review_col_turns")}
                    </th>
                    <th className="px-3 py-3 font-medium">
                      {translate("staff_voice_review_col_status")}
                    </th>
                    <th className="px-3 py-3 font-medium">
                      {translate("staff_console_col_open")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {voiceSessions.map((row) => (
                    <tr
                      key={row.id}
                      className={`border-b border-slate-200 ${
                        row.id === Number(selectedConversationId)
                          ? "bg-slate-50"
                          : ""
                      }`}
                    >
                      <td className="px-3 py-4 text-slate-700">
                        {formatDateTime(row.started_at)}
                      </td>
                      <td className="px-3 py-4 text-slate-950">
                        {row.user_name}
                      </td>
                      <td className="px-3 py-4 text-slate-700">
                        {row.language}
                      </td>
                      <td className="px-3 py-4 text-slate-700">
                        {`${Math.round(row.duration_seconds / 60)}m ${row.duration_seconds % 60}s`}
                      </td>
                      <td className="px-3 py-4 text-slate-700">
                        {formatNumber(row.turn_count)}
                      </td>
                      <td className="px-3 py-4">
                        <Badge tone={row.is_active ? "good" : "neutral"}>
                          {row.is_active ? "active" : "ended"}
                        </Badge>
                      </td>
                      <td className="px-3 py-4">
                        <button
                          type="button"
                          onClick={() => setSelectedConversationId(String(row.id))}
                          className="inline-flex items-center gap-2 border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                        >
                          {translate("staff_voice_review_open")}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
            <div className="space-y-4">
              <h2 className="text-lg font-semibold tracking-tight text-slate-950">
                {translate("staff_voice_review_conversation_title")}
              </h2>
              <table className="w-full text-sm">
                <tbody>
                  <MetricRow
                    label={translate("staff_voice_review_detail_id")}
                    value={selectedConversation ? String(selectedConversation.id) : "-"}
                  />
                  <MetricRow
                    label={translate("staff_voice_review_detail_prompt")}
                    value={selectedConversation?.situation_label_de ?? "-"}
                  />
                  <MetricRow
                    label={translate("staff_voice_review_detail_outcome")}
                    value={selectedConversation?.user_name ?? "-"}
                  />
                  <MetricRow
                    label={translate("staff_voice_review_detail_duration")}
                    value={
                      selectedConversation
                        ? `${Math.round(selectedConversation.duration_seconds / 60)}m ${selectedConversation.duration_seconds % 60}s`
                        : "-"
                    }
                  />
                  <MetricRow
                    label={translate("staff_voice_review_detail_tokens")}
                    value={selectedConversation ? formatNumber(selectedConversation.turn_count) : "-"}
                  />
                  <MetricRow
                    label={translate("staff_voice_review_detail_tags")}
                    value={selectedConversation ? `${selectedConversation.username}, ${selectedConversation.active_branch || "greeting"}` : "-"}
                  />
                </tbody>
              </table>
              <div className="rounded-none border border-slate-200 p-4">
                <p className="text-xs font-medium normal-case tracking-[0.08em] text-slate-600">
                  {translate("staff_voice_review_detail_timeline")}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-slate-700">
                  {translate("staff_voice_review_detail_timeline_body")}
                </p>
              </div>
              <div className="rounded-none border border-slate-200 p-4">
                <p className="text-xs font-medium normal-case tracking-[0.08em] text-slate-600">
                  {translate("staff_voice_review_detail_transcript")}
                </p>
                <div className="mt-3 space-y-3">
                  {selectedConversation && selectedConversation.turns.length > 0 ? (
                    selectedConversation.turns.map((turn, index) => (
                      <div key={`${turn.timestamp}-${index}`} className="border-b border-slate-200 pb-3 last:border-b-0 last:pb-0">
                        <p className="text-xs font-medium normal-case tracking-[0.08em] text-slate-600">
                          {formatConversationTurnLabel(turn.role)}
                        </p>
                        <p className="mt-1 text-sm leading-relaxed text-slate-800">
                          {turn.text || "—"}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          {turn.timestamp ? formatDateTime(turn.timestamp) : ""}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm leading-relaxed text-slate-700">
                      {translate("staff_voice_review_detail_transcript_empty")}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h2 className="text-lg font-semibold tracking-tight text-slate-950">
                {translate("staff_voice_review_pattern_title")}
              </h2>
              <table className="w-full text-sm">
                <tbody>
                  <MetricRow
                    label={translate("staff_voice_review_pattern_category")}
                    value={selectedPattern.category}
                  />
                  <MetricRow
                    label={translate("staff_voice_review_pattern_count")}
                    value={formatNumber(selectedPattern.relatedConversations)}
                  />
                  <MetricRow
                    label={translate("staff_voice_review_pattern_languages")}
                    value={selectedPattern.languages}
                  />
                  <MetricRow
                    label={translate("staff_voice_review_pattern_priority")}
                    value={selectedPattern.priority}
                  />
                </tbody>
              </table>
              <p className="text-sm leading-relaxed text-slate-700">
                {selectedPattern.explanation}
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="text-xs normal-case tracking-[0.08em] text-slate-600">
                <tr className="border-b border-slate-200">
                  <th className="px-3 py-3 font-medium">
                    {translate("staff_voice_review_change_type")}
                  </th>
                  <th className="px-3 py-3 font-medium">
                    {translate("staff_voice_review_change_current")}
                  </th>
                  <th className="px-3 py-3 font-medium">
                    {translate("staff_voice_review_change_candidate")}
                  </th>
                  <th className="px-3 py-3 font-medium">
                    {translate("staff_voice_review_change_status")}
                  </th>
                  <th className="px-3 py-3 font-medium">
                    {translate("staff_console_col_open")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {changes.map((row) => (
                  <tr
                    key={row.id}
                    className={`border-b border-slate-200 ${
                      row.id === selectedChange.id ? "bg-slate-50" : ""
                    }`}
                  >
                    <td className="px-3 py-4 text-slate-950">{row.type}</td>
                    <td className="px-3 py-4 text-slate-700">
                      {row.currentPromptVersion}
                    </td>
                    <td className="px-3 py-4 text-slate-700">
                      {row.proposedPromptVersion}
                    </td>
                    <td className="px-3 py-4">
                      <Badge
                        tone={
                          row.evaluationStatus === "Evaluation ready"
                            ? "good"
                            : "neutral"
                        }
                      >
                        {row.evaluationStatus}
                      </Badge>
                    </td>
                    <td className="px-3 py-4">
                      <button
                        type="button"
                        onClick={() => setSelectedChangeId(row.id)}
                        className="inline-flex items-center gap-2 border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                      >
                        {translate("staff_voice_review_open")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <div className="rounded-none border border-slate-200 p-4 lg:col-span-2">
              <p className="text-xs font-medium normal-case tracking-[0.08em] text-slate-600">
                {translate("staff_voice_review_change_title")}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-slate-700">
                {selectedChange.diff}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-slate-700">
                {selectedChange.reason}
              </p>
            </div>
            <div className="rounded-none border border-slate-200 p-4">
              <p className="text-xs font-medium normal-case tracking-[0.08em] text-slate-600">
                {translate("staff_voice_review_actions")}
              </p>
              <ul className="mt-2 space-y-2 text-sm leading-relaxed text-slate-700">
                <li>{translate("staff_voice_review_action_approve")}</li>
                <li>{translate("staff_voice_review_action_discard")}</li>
                <li>{translate("staff_voice_review_action_defer")}</li>
                <li>{translate("staff_voice_review_action_revise")}</li>
              </ul>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
